import { plans, plansName } from '@thecompaniesapi/shared/client'

export const useTeamsStore = defineStore('teams', () => {
  const api = useApi()

  const apiTokens = ref<ApiToken[]>([])
  const currentTeamId = ref<null | number>(null)
  const teamInvitations = ref<TeamInvitation[]>([])
  const teamNotifications = ref<null | TeamNotifications>(null)
  const teams = ref<Team[]>([])
  const teamSettings = ref<null | TeamSettings>(null)
  const teamUsers = ref<User[]>([])
  const webhooks = ref<Webhook[]>([])

  const currentPlan = computed<Plan>(() => {
    const environment = useRuntimeConfig().public.nuxtEnv === 'development' ? 'development' : 'production'

    if (!currentTeam.value) {
      return plans.free
    }

    if (!currentTeam.value?.stripeProductId) {
      return plans.free
    }

    for (const planName of plansName) {
      if (currentTeam.value?.stripeProductId === plans[planName][environment].id) {
        return plans[planName]
      }
    }

    return plans.free
  })

  const currentTeam = computed(() => {
    return teams.value.find(team => team.id === currentTeamId.value)
  })

  const createTeam = async (params: {
    name: string
  }) => {
    try {
      const { data: team } = await api.teams.create(params)

      addOrReplaceTeams({ teams: [team] })

      await switchTeam({ teamId: team.id })
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const createTeamApiToken = async (params: {
    name: string
  }) => {
    try {
      if (currentTeamId.value) {
        const { data: apiToken } = await api.teams.apiTokens.create(currentTeamId.value, { name: params.name })

        apiTokens.value.unshift(apiToken)
      }
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const createTeamInvitation = async (params: {
    email: string
  }) => {
    try {
      if (currentTeamId.value) {
        const { data: teamInvitation } = await api.teams.invitations.create(currentTeamId.value, { email: params.email })

        teamInvitations.value.push(teamInvitation)
      }
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const createTeamWebhook = async (params: {
    listId?: number
    name: string
  }) => {
    try {
      if (currentTeamId.value) {
        const { data: webhook } = await api.teams.webhooks.create(currentTeamId.value, {
          listId: params.listId,
          name: params.name,
        })

        webhooks.value.push(webhook)
      }
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const fetchTeam = async (params: {
    teamId: number
  }) => {
    try {
      const { data: team } = await api.teams.fetchOne(params.teamId)

      addOrReplaceTeams({ teams: [team] })
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const fetchTeamApiTokens = async () => {
    try {
      if (currentTeamId.value) {
        const { data: apiTokensFetched } = await api.teams.apiTokens.fetch(currentTeamId.value)

        apiTokens.value = apiTokensFetched
      }
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const fetchTeamDependencies = async () => {
    const listsStore = useListsStore()
    const billingStore = useBillingStore()
    const actionsStore = useActionsStore()

    // TODO: Implement integrations
    // const integrationsStore = useIntegrationsStore()

    try {
      await Promise.all([
        fetchTeamApiTokens(),
        fetchTeamNotifications(),
        fetchTeamSettings(),
        fetchTeamUsers(),
        fetchTeamWebhooks(),
        fetchTeamInvitations(),
        listsStore.fetchLists({ size: 500 }),
        billingStore.fetchSubscriptions(),
        actionsStore.fetchActions(),

        // Disabled
        // integrationsStore.fetchIntegrations({ teamOnly: true }),
      ])
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const fetchTeamInvitations = async () => {
    try {
      if (currentTeamId.value) {
        const { data: teamInvitationsFetched } = await api.teams.invitations.fetch(currentTeamId.value)

        teamInvitations.value = teamInvitationsFetched
      }
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const fetchTeamNotifications = async () => {
    try {
      if (currentTeamId.value) {
        const { data: teamNotificationsFetched } = await api.teams.notifications.fetch(currentTeamId.value)

        teamNotifications.value = teamNotificationsFetched
      }
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const fetchTeams = async () => {
    try {
      const { data: teamsFetched } = await api.teams.fetch()

      addOrReplaceTeams({ teams: teamsFetched })
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const fetchTeamSettings = async () => {
    try {
      if (currentTeamId.value) {
        const { data: teamSettingsFetched } = await api.teams.settings.fetch(currentTeamId.value)

        teamSettings.value = teamSettingsFetched
      }
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const fetchTeamUsers = async () => {
    try {
      if (currentTeamId.value) {
        const { data: teamUserFetched } = await api.teams.users.fetch(currentTeamId.value)

        teamUsers.value = teamUserFetched
      }
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const fetchTeamWebhooks = async () => {
    try {
      if (currentTeamId.value) {
        const { data: webhooksFetched } = await api.teams.webhooks.fetch(currentTeamId.value)

        webhooks.value = webhooksFetched
      }
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const removeTeamApiToken = async (params: {
    apiTokenId: number
  }) => {
    try {
      if (currentTeamId.value) {
        await api.teams.apiTokens.delete(currentTeamId.value, params.apiTokenId)

        apiTokens.value = apiTokens.value.filter(apiToken => apiToken.id !== params.apiTokenId)
      }
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const removeTeamInvitation = async (params: {
    invitationId: number
  }) => {
    try {
      if (currentTeamId.value) {
        await api.teams.invitations.delete(currentTeamId.value, params.invitationId)

        teamInvitations.value = teamInvitations.value.filter(teamInvitation => teamInvitation.id !== params.invitationId)
      }
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const removeTeamUser = async (params: {
    userId: number
  }) => {
    const userStore = useUserStore()

    try {
      if (currentTeamId.value) {
        await api.teams.users.delete(currentTeamId.value, params.userId)

        teamUsers.value = teamUsers.value.filter(teamUser => teamUser.id !== params.userId)

        // Switch to another team when leaving
        if (userStore.user && userStore.user.id === params.userId) {
          teams.value = teams.value.filter(team => team.id !== currentTeamId.value)

          if (teams.value.length) {
            await switchTeam({ teamId: teams.value[0].id })
          }
          else {
            resetCurrentTeam()

            userStore.user = {
              ...userStore.user,
              currentTeamId: null,
            }
          }
        }
      }
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const removeTeamWebhook = async (params: {
    webhookId: number
  }) => {
    try {
      if (currentTeamId.value) {
        await api.teams.webhooks.delete(currentTeamId.value, params.webhookId)

        webhooks.value = webhooks.value.filter(webhook => webhook.id !== params.webhookId)
      }
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const sendTeamWebhookTestEvent = async (params: {
    webhookId: number
  }) => {
    try {
      if (currentTeamId.value) {
        await api.teams.webhooks.sentTestEvent(currentTeamId.value, params.webhookId)
      }
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const switchTeam = async (params: {
    teamId: number
  }) => {
    const userStore = useUserStore()

    try {
      const { data: team } = await api.teams.switch(params.teamId)

      if (team) {
        resetCurrentTeamDependencies()

        currentTeamId.value = team.id

        if (userStore.user) {
          userStore.user = {
            ...userStore.user,
            currentTeamId: team.id,
          }
        }

        await fetchTeamDependencies()
      }
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const updateTeam = async (params: {
    country?: string
    name?: string
    teamId: number
    websiteUrl?: string
  }) => {
    try {
      const { data: team } = await api.teams.update(params.teamId, {
        country: params.country,
        name: params.name,
        websiteUrl: params.websiteUrl,
      })

      addOrReplaceTeams({ teams: [team] })

      return team
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const updateTeamApiToken = async (params: {
    apiTokenId: number
    name: string
  }) => {
    try {
      if (currentTeamId.value) {
        const { data: updatedApiToken } = await api.teams.apiTokens.update(currentTeamId.value, params.apiTokenId, { name: params.name })

        apiTokens.value = apiTokens.value.map((apiToken) => {
          return apiToken.id === params.apiTokenId ? updatedApiToken : apiToken
        })

        return updatedApiToken
      }
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const updateTeamWebhook = async (params: {
    format?: WebhookFormat
    listsToAdd?: number[]
    listsToRemove?: number[]
    name?: string
    url?: string
    webhookId: number
  }) => {
    try {
      if (currentTeamId.value) {
        const { data: webhookFetched } = await api.teams.webhooks.update(currentTeamId.value, params.webhookId, {
          format: params.format,
          listsToAdd: params.listsToAdd,
          listsToRemove: params.listsToRemove,
          name: params.name,
          url: params.url,
        })

        webhooks.value = webhooks.value.map(webhook => webhook.id === params.webhookId ? webhookFetched : webhook)

        return webhookFetched
      }
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  // Setters
  const addOrReplaceTeams = (params: {
    teams: Team[]
  }) => {
    for (const team of params.teams) {
      const index = teams.value.findIndex(item => item.id === team.id)

      index === -1 ? teams.value.push(team) : teams.value.splice(index, 1, team)
    }
  }

  const resetCurrentTeam = () => {
    apiTokens.value = []
    currentTeamId.value = null
    teamInvitations.value = []
    teamNotifications.value = null
    teamSettings.value = null
    teamUsers.value = []
    webhooks.value = []
  }

  const resetCurrentTeamDependencies = () => {
    const billingStore = useBillingStore()
    const actionsStore = useActionsStore()
    const listsStore = useListsStore()

    resetCurrentTeam()

    billingStore.resetStore()
    actionsStore.resetStore()
    listsStore.resetStore()
  }

  const resetStore = () => {
    teams.value = []

    resetCurrentTeam()
  }

  return {
    addOrReplaceTeams,
    apiTokens,
    createTeam,
    createTeamApiToken,
    createTeamInvitation,
    createTeamWebhook,
    currentPlan,
    currentTeam,
    currentTeamId,
    fetchTeam,
    fetchTeamApiTokens,
    fetchTeamDependencies,
    fetchTeamInvitations,
    fetchTeamNotifications,
    fetchTeams,
    fetchTeamSettings,
    fetchTeamUsers,
    fetchTeamWebhooks,
    removeTeamApiToken,
    removeTeamInvitation,
    removeTeamUser,
    removeTeamWebhook,
    resetCurrentTeam,
    resetCurrentTeamDependencies,
    resetStore,
    sendTeamWebhookTestEvent,
    switchTeam,
    teamInvitations,
    teamNotifications,
    teams,
    teamSettings,
    teamUsers,
    updateTeam,
    updateTeamApiToken,
    updateTeamWebhook,
    webhooks,
  }
})
